import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { chambreFroide, climatiseur, electrique, meubleBureau, rehabilitation, solaireImage, def1, def2, def3, def4, def5, def6, def7, def8, def9 } from '../../assets/images';
import { ChevronLeft, ChevronRight, Mail } from 'lucide-react';

const Projects = () => {
  const { i18n } = useTranslation();
  const isEng = i18n.language && i18n.language.startsWith('en');

  const projets = [
    {
      titre: isEng ? "Photovoltaic Installation" : "Installation Photovoltaïque",
      categorie: isEng ? "Solar Energy" : "Énergie Solaire",
      description: isEng ? "Design and installation of sustainable solar systems for carbon footprint reduction" : "Conception et installation de systèmes solaires durables pour réduction d'empreinte carbone",
      image: solaireImage,
    },
    {
      titre: isEng ? "Industrial Cold Rooms" : "Chambres Froides Industrielles",
      categorie: isEng ? "Cold & Air Conditioning" : "Froid & Climatisation",
      description: isEng ? "Maintenance and installation of cold rooms complying with industrial standards" : "Maintenance et installation de chambres froides conformes aux normes industrielles",
      image: chambreFroide,
    },
    {
      titre: isEng ? "Architectural Renovation" : "Rénovation Architecturale",
      categorie: isEng ? "Rehabilitation" : "Réhabilitation",
      description: isEng ? "Complete transformation including acoustic partitioning and aluminum window installation" : "Transformation complète incluant cloisonnement acoustique et implantation de fenêtres en aluminium",
      image: rehabilitation,
    },
    {
      titre: isEng ? "Premium Office Furniture" : "Meubles de Bureau Premium",
      categorie: isEng ? "Carpentry" : "Menuiserie",
      description: isEng ? "Design and manufacture of partitioned melamine office tables adapted to modern professional spaces" : "Conception et fabrication de tables de bureau en mélamine cloisonnée adaptées aux espaces professionnels modernes",
      image: meubleBureau,
    },
    {
      titre: isEng ? "Commercial Air Conditioning" : "Climatisation Commerciale",
      categorie: isEng ? "Cold & Air Conditioning" : "Froid & Climatisation",
      description: isEng ? "Installation of ceiling air conditioning systems for high-traffic commercial spaces" : "Installation de systèmes de climatisation au plafond pour espaces commerciaux à fort trafic",
      image: climatiseur,
    },
    {
      titre: isEng ? "Electrical Infrastructure" : "Infrastructure Électrique",
      categorie: isEng ? "Electricity" : "Électricité",
      description: isEng ? "Installation of industrial electrical switchboards with automation systems" : "Installation de tableaux électriques industriels avec systèmes d'automatisation",
      image: electrique,
    }
  ];

  const bgSlides = [def1, def2, def3, def4, def5, def6, def7, def8, def9];
  const [bgSlide, setBgSlide] = useState(2);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const itemsPerPage = isMobile ? 1 : 3;
  const totalPages = Math.ceil(projets.length / itemsPerPage);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (totalPages <= 1 || isPaused) return;
    const interval = setInterval(() => setCurrentIndex(i => (i + 1) % totalPages), 5000);
    return () => clearInterval(interval);
  }, [currentIndex, totalPages, isPaused]);

  useEffect(() => {
    const interval = setInterval(() => {
      setBgSlide((prev) => (prev + 1) % bgSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [bgSlides.length]);

  const goTo = (i) => setCurrentIndex(i < 0 ? totalPages - 1 : i % totalPages);

  const handleMailRequest = (e, projectTitle) => {
    e.preventDefault();
    e.stopPropagation();
    const subject = isEng 
      ? `Catalog Request - ${projectTitle}` 
      : `Demande de catalogue - ${projectTitle}`;
    const body = isEng 
      ? `Hello,\n\nI would like to receive the detailed catalog for the project: ${projectTitle}.\n\nThank you,` 
      : `Bonjour,\n\nJe souhaiterais recevoir le catalogue détaillé concernant le projet : ${projectTitle}.\n\nMerci,`;
    
    window.location.href = `mailto:services@auxis.mg?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <section id="projects" className="section-padding bg-white dark:bg-auxis-charcoal relative overflow-hidden transition-colors duration-300">
      <div className="absolute inset-0">
        <AnimatePresence mode="wait">
          <motion.img 
            key={bgSlide}
            src={bgSlides[bgSlide]} 
            alt="AUXIS Background"
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-r from-auxis-charcoal/90 via-auxis-charcoal/70 to-auxis-charcoal/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-auxis-charcoal via-transparent to-auxis-charcoal/30" />
      </div>

      <div className="container-wide relative z-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 md:mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-2xl"
          >
            <span className="inline-flex items-center gap-2.5 text-caption uppercase font-bold tracking-[0.15em] text-auxis-gold mb-5">
              <span className="w-2 h-2 rounded-full bg-auxis-gold" />
              Portfolio
            </span>
            <h2 className="text-display text-white mb-5">
              {isEng ? 'Our completed projects' : 'Nos projets réalisés'}
            </h2>
            <div className="gold-line mb-6" />
            <p className="text-body-lg text-gray-300 max-w-2xl">
              {isEng ? 'Discover our portfolio of successful projects in various sectors.' : 'Découvrez notre portfolio de projets réussis dans différents secteurs.'}
            </p>
          </motion.div>

          {totalPages > 1 && (
            <div className="hidden md:flex items-center gap-3">
              <button
                onClick={() => goTo(currentIndex - 1)}
                className="w-11 h-11 rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-white/10 hover:border-auxis-gold/50 transition-all duration-300"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={() => goTo(currentIndex + 1)}
                className="w-11 h-11 rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-white/10 hover:border-auxis-gold/50 transition-all duration-300"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          )}
        </div>

        <div
          className="overflow-hidden cursor-grab active:cursor-grabbing"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          onTouchStart={() => setIsPaused(true)}
          onTouchEnd={() => setTimeout(() => setIsPaused(false), 1000)}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              className="grid grid-cols-1 md:grid-cols-3 gap-6"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.2}
              onDragEnd={(e, { offset, velocity }) => {
                const swipe = offset.x * velocity.x;
                if (offset.x < -50 || swipe < -10000) {
                  goTo(currentIndex + 1);
                } else if (offset.x > 50 || swipe > 10000) {
                  goTo(currentIndex - 1);
                }
              }}
            >
              {projets.slice(
                currentIndex * itemsPerPage,
                Math.min((currentIndex + 1) * itemsPerPage, projets.length)
              ).map((projet, index) => (
                <motion.div
                  key={`${projet.titre}-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="group relative rounded-2xl overflow-hidden aspect-[4/3]"
                >
                  <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-700 ease-out-expo group-hover:scale-105"
                    style={{ backgroundImage: `url(${projet.image})` }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-auxis-charcoal/90 via-auxis-charcoal/20 to-transparent opacity-70 group-hover:opacity-90 transition-opacity duration-500" />
                  <div className="absolute inset-0 p-6 flex flex-col justify-end">
                    <span className="inline-block self-start px-3 py-1 mb-3 text-caption uppercase font-semibold tracking-wider bg-auxis-gold/90 text-white rounded-full">
                      {projet.categorie}
                    </span>
                    <h3 className="text-xl md:text-2xl font-bold text-white mb-2">
                      {projet.titre}
                    </h3>
                    <p className="text-sm text-white/70 mb-4 line-clamp-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 translate-y-2 group-hover:translate-y-0">
                      {projet.description}
                    </p>
                    <button
                      onClick={(e) => handleMailRequest(e, projet.titre)}
                      className="inline-flex items-center gap-2 text-sm font-medium text-auxis-gold opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0"
                    >
                      <Mail size={14} />
                      {isEng ? 'Request catalog by email' : 'Demander le catalogue par mail'}
                    </button>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>

        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-10">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className={`h-1.5 rounded-full transition-all duration-300 ${currentIndex === i ? 'w-8 bg-auxis-gold' : 'w-1.5 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Projects;
